import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, Sparkles, CalendarDays, Clock, KeyRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USER_KEY = "soarlife-client-user";
const TOKEN_KEY = "soarlife-client-token";

export default function PaymentReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "polling");
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");
  const tries = useRef(0);

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (isCancel) return;
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setInfo(res.data);
        if (res.data.payment_status === "paid") {
          if (res.data.kind === "clinic") {
            try {
              const c = JSON.parse(localStorage.getItem("soarlife-clinic") || "null");
              if (c) {
                localStorage.setItem("soarlife-clinic", JSON.stringify({ ...c, subscription_status: "active" }));
              }
            } catch {}
            setState("clinic_paid");
            timer = setTimeout(() => navigate("/clinic"), 2200);
          } else if (res.data.kind === "supplement") {
            setState("order_confirmed");
          } else if (res.data.kind === "interpreter_request") {
            setState("interpreter_confirmed");
          } else if (res.data.kind !== "booking") {
            // Membership: unlock locally so the portal opens immediately
            try {
              const u = JSON.parse(localStorage.getItem(USER_KEY) || "null");
              if (u) {
                const merged = { ...u, access_granted: true, membership_status: "active" };
                localStorage.setItem(USER_KEY, JSON.stringify(merged));
              }
            } catch {}
            setState("paid");
            timer = setTimeout(() => navigate("/portal"), 1800);
          } else {
            setState(res.data.grants_portal ? "claim" : "booking_confirmed");
          }
          return;
        }
      } catch {}
      if (tries.current < 15) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("timeout");
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claimAccount = async (e) => {
    e.preventDefault();
    if (claiming || password.length < 8) return;
    setClaiming(true);
    setClaimError("");
    try {
      const res = await axios.post(`${API}/clients/claim`, { session_id: sessionId, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.client));
      navigate("/portal");
    } catch (err) {
      setClaimError(err?.response?.data?.detail || "Could not create your account — please try again.");
      setClaiming(false);
    }
  };

  const appt = info?.appointment;
  const manageUrl = appt?.manage_token ? `/book/manage?token=${appt.manage_token}` : "/book";

  return (
    <div className="min-h-screen bg-[#3F5043] flex items-center justify-center p-5">
      <div
        data-testid="payment-return-screen"
        className="w-full max-w-sm bg-[#F8F7F3] rounded-3xl p-8 shadow-2xl text-center"
      >
        {state === "polling" && (
          <>
            <Loader2 size={36} className="animate-spin text-[#875F45] mx-auto mb-4" />
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2">Confirming your payment…</div>
            <p className="text-sm text-[#3F5043]/70" data-testid="payment-polling-text">
              Stripe is confirming your payment. This takes a few seconds.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#AE8F78]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="payment-success-title">
              Welcome to Soarlife
            </div>
            <p className="text-sm text-[#3F5043]/70 mb-5">
              Your membership is active. Your portal — Soarlife AI, your Fertility Roadmap, records vault and care team — is unlocked.
            </p>
            <Link
              to="/portal"
              data-testid="payment-success-continue"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
            >
              <Sparkles size={15} /> Enter your portal
            </Link>
          </>
        )}

        {state === "claim" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mx-auto mb-4">
              <KeyRound size={24} className="text-[#AE8F78]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="claim-title">
              Your package is confirmed
            </div>
            {appt && (
              <p className="text-sm text-[#3F5043]/70 mb-4" data-testid="claim-appointment">
                {appt.title} · {appt.date} at {appt.time} ET
              </p>
            )}
            <p className="text-sm text-[#3F5043]/70 mb-4">
              Your purchase includes the Soarlife Patient Portal. Create a password to unlock your
              Fertility Roadmap, AI concierge, records vault and care team.
            </p>
            <form onSubmit={claimAccount} data-testid="claim-form" className="space-y-3">
              <input
                data-testid="claim-password"
                type="password"
                required
                minLength={8}
                placeholder="Create a password (8+ characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]"
              />
              {claimError && (
                <p className="text-xs text-red-700" data-testid="claim-error">{claimError}</p>
              )}
              <button
                type="submit"
                data-testid="claim-submit"
                disabled={claiming || password.length < 8}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40"
              >
                {claiming ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                Create my account & enter portal
              </button>
            </form>
            <p className="text-[11px] text-[#3F5043]/50 mt-4">
              All sales are final — you can reschedule anytime from your confirmation link.
            </p>
          </>
        )}

        {state === "booking_confirmed" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#AE8F78]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="booking-success-title">
              You're booked
            </div>
            {appt && (
              <div className="text-sm text-[#3F5043]/80 mb-4 space-y-1" data-testid="booking-success-details">
                <div className="font-medium">{appt.title}</div>
                <div className="flex items-center justify-center gap-3 text-[#3F5043]/60">
                  <span className="inline-flex items-center gap-1"><CalendarDays size={13} /> {appt.date}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={13} /> {appt.time} ET</span>
                </div>
              </div>
            )}
            <p className="text-xs text-[#875F45] mb-5 leading-relaxed">
              All sales are final. Need a different time? Reschedule at no charge from your manage link.
            </p>
            {info?.lookup_key === "first-consult" && (
              <div className="mb-5 text-left" data-testid="package-upsell">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#AE8F78] font-semibold mb-2 text-center">
                  Most families continue with
                </div>
                <div className="space-y-2">
                  <Link
                    to="/book?service=iui-support"
                    data-testid="upsell-iui"
                    className="block bg-white border border-[#BCB3A6]/40 rounded-2xl p-4 hover:border-[#899480] transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-base text-[#3F5043]">IUI Support Package</span>
                      <span className="font-serif text-lg text-[#875F45]">$800</span>
                    </div>
                    <p className="text-[11px] text-[#3F5043]/60 mt-0.5">
                      Full-cycle support + Patient Portal access
                    </p>
                  </Link>
                  <Link
                    to="/book?service=ivf-support"
                    data-testid="upsell-ivf"
                    className="block bg-[#3F5043] rounded-2xl p-4 hover:bg-[#875F45] transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-base text-[#F8F7F3]">IVF Support Package</span>
                      <span className="font-serif text-lg text-[#AE8F78]">$1,250</span>
                    </div>
                    <p className="text-[11px] text-[#F8F7F3]/70 mt-0.5">
                      Concierge care through your whole cycle + Patient Portal access
                    </p>
                  </Link>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Link
                to={manageUrl}
                data-testid="booking-manage-link"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
              >
                Manage / reschedule my booking
              </Link>
              <Link
                to="/"
                className="block text-xs text-[#3F5043]/60 hover:text-[#3F5043] pt-1"
              >
                Back to home
              </Link>
            </div>
          </>
        )}

        {state === "interpreter_confirmed" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#AE8F78]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="interpreter-success-title">
              Interpreter confirmed
            </div>
            <p className="text-sm text-[#3F5043]/70 mb-5">
              Payment received — your in-person interpreter is confirmed. Soarlife will meet you at your
              appointment and text you any updates.
            </p>
            <Link
              to="/portal"
              data-testid="interpreter-success-continue"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
            >
              Back to your portal
            </Link>
          </>
        )}

        {state === "order_confirmed" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#AE8F78]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="order-success-title">
              Order confirmed
            </div>
            <p className="text-sm text-[#3F5043]/70 mb-5">
              Thank you — your supplement order is confirmed. Your Soarlife nurse reviews it against
              your protocol, and we'll text you when it ships.
            </p>
            <Link
              to="/learn"
              data-testid="order-success-continue"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
            >
              Back to the Education Center
            </Link>
          </>
        )}

        {state === "clinic_paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#AE8F78]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="clinic-paid-title">
              Partner plan active
            </div>
            <p className="text-sm text-[#3F5043]/70 mb-5">
              Your $199/month Clinic Partner subscription is live. Interpreter requests and your
              partner dashboard are unlocked.
            </p>
            <Link
              to="/clinic"
              data-testid="clinic-paid-continue"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
            >
              Open your clinic portal
            </Link>
          </>
        )}

        {state === "cancelled" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#875F45]/10 flex items-center justify-center mx-auto mb-4">
              <XCircle size={24} className="text-[#875F45]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="payment-cancel-title">
              Checkout cancelled
            </div>
            <p className="text-sm text-[#3F5043]/70 mb-5">
              No charge was made. You can complete your booking anytime.
            </p>
            <Link
              to="/book"
              data-testid="payment-cancel-back"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
            >
              Back to booking
            </Link>
          </>
        )}

        {(state === "error" || state === "timeout") && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#875F45]/10 flex items-center justify-center mx-auto mb-4">
              <XCircle size={24} className="text-[#875F45]" />
            </div>
            <div className="font-serif italic text-2xl text-[#3F5043] mb-2" data-testid="payment-error-title">
              {state === "timeout" ? "Still confirming…" : "Something went wrong"}
            </div>
            <p className="text-sm text-[#3F5043]/70 mb-5">
              {state === "timeout"
                ? "Your payment is still being confirmed. If you were charged, your booking will appear shortly — check your email."
                : "We couldn't verify this payment session. If you completed checkout, your confirmation will arrive by email shortly."}
            </p>
            <Link
              to="/book"
              data-testid="payment-error-back"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
            >
              Go to booking
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
